import React from 'react';
import { useObserver } from 'mobx-react';

import { analytics } from 'core/analytics';

import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { GltfViewerReload } from 'libs/rendering/gltf/api/errors';

import Button from 'components/Button';
import { ButtonGroup } from 'apps/components/shared';

import { SystemMessageStyled, Kbd } from './systemMessage.styled';

export default function SystemMessage(props) {
  const viewerControl = useViewerControl();

  return useObserver(() => {
    const status = props.status || viewerControl.conversionStatus;
    const { progress } = props;
    if (['MISSING', 'PENDING'].includes(status)) {
      if (progress) {
        return (
          <SystemMessageStyled
            type='warning'
            top={props.top}
            canvasWidth={props.canvasWidth}
          >
            <strong>Preparing your file.</strong> Just a sec...
          </SystemMessageStyled>
        );
      }
      return (
        <SystemMessageStyled
          type='warning'
          top={props.top}
          canvasWidth={props.canvasWidth}
        >
          <strong>We are waiting for progress updates.</strong>
        </SystemMessageStyled>
      );
    }

    // - ERROR refers to conversion error, so we just display an error message
    // without reload button, because the viewer is not in an exceptional state
    // that could be fixed by a reload (most likely, could be a network problem)
    if (['ERROR'].includes(status)) {
      return (
        <SystemMessageStyled type='error' canvasWidth={props.canvasWidth}>
          <strong>Your CAD Room encountered an internal error.</strong>{' '}
          We&apos;ll look into it straight away. Sorry for the inconvenience!
        </SystemMessageStyled>
      );
    }

    // - I pass in EXCEPTION now when the viewer is in an exceptional state, cause
    // those can be caused by undefined behaviour on the c++ side, a reload might
    // fix the problem at least for the time being
    // - I also send an extra message to slack in that case so we can see if people
    // are experiencing exceptions and then click the reload button
    if (['EXCEPTION'].includes(status)) {
      return (
        <SystemMessageStyled type='error' canvasWidth={props.canvasWidth}>
          <strong>Your CAD Room encountered an internal error.</strong>{' '}
          We&apos;ll look into it straight away. Sorry for the inconvenience!
          <br />
          <ButtonGroup spacing='small' alignment='center'>
            <Button
              text='Reload'
              size='small'
              outline
              onClick={() => {
                analytics.logError(
                  new GltfViewerReload('user requested reload after exception'),
                );
                window.location.reload();
              }}
            />
          </ButtonGroup>
        </SystemMessageStyled>
      );
    }

    if (['TIMEOUT'].includes(status)) {
      return (
        <SystemMessageStyled type='error' canvasWidth={props.canvasWidth}>
          <strong>Timeout while waiting for CAD Room to become ready.</strong>{' '}
        </SystemMessageStyled>
      );
    }

    if (viewerControl.offline) {
      return (
        <SystemMessageStyled type='warning' canvasWidth={props.canvasWidth}>
          <strong>CAD Room offline.</strong> Re-connecting...
        </SystemMessageStyled>
      );
    }

    const selModeLinkPromise =
      viewerControl.viewer?.selectionManager?.IsSelectionModeLink();
    const selModePromise =
      viewerControl.viewer?.selectionManager?.GetSelectionMode();

    let selModeLink = null;
    let selMode = null;
    if (selModeLinkPromise && selModePromise) {
      Promise.all([selModeLinkPromise, selModePromise]).then(values => {
        [selModeLink, selMode] = values;
      });
    }
    if (selModeLink && selMode) {
      const selectionEnum = viewerControl.viewer.module.SelectionMode;
      const modeLabel =
        selMode === selectionEnum.SelectionModeLink ? 'Feature' : 'Part';
      return (
        <SystemMessageStyled type='info' canvasWidth={props.canvasWidth}>
          <strong>You&apos;re in {modeLabel} Annotation mode</strong>. Press{' '}
          <Kbd type='info'>Esc</Kbd> to exit.
        </SystemMessageStyled>
      );
    }

    return null;
  });
}
